import React, { memo } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'i18n';
import { Step } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { CollapsibleStepper } from 'components/common/CollapsibleStepper';
import StepLabel from 'components/common/StepLabel';
import StepIcon from 'components/common/StepIcon';
import { routes } from 'utils/routing';

interface IProps {
  activeStep?: number;
  collapsed?: boolean;
}

// JPA-7186: `activeStep` used to be hardcoded per screen (EventTicketsScreen/EventOrderScreen/
// EventRegistrationScreen), and `EventOrderScreen` is reused both before registration
// (`ticketsSummary`) and after it (`order/summary`, `order/send-to`) - one hardcoded value could
// never be correct for both, which is why "Registration" was marked completed a step too early.
// The route pathname is the single source of truth for which checkout step is active instead.
const STEP_BY_PATHNAME: Record<string, number> = {
  [routes.eventTickets(0).href.pathname]: 0,
  [routes.eventTicketsSummary(0).href.pathname]: 0,
  [routes.eventRegistration(0).href.pathname]: 1,
  [routes.eventOrderSummary(0).href.pathname]: 2,
  [routes.eventOrderSendTo(0).href.pathname]: 2,
  [routes.eventOrderPaymentMethod(0).href.pathname]: 2,
};

const useStyles = makeStyles()((theme) => ({
  stepper: {
    [theme.breakpoints.down('md')]: {
      position: 'relative',
      borderBottom: `1px solid ${theme.palette.custom.gray.light}`,
      width: '100%',
      zIndex: theme.zIndex.modal,
      top: 0,
      padding: '0 8px 0 0',
    },
  },
}));

const CheckoutCollapsibleStepper: React.FC<React.PropsWithChildren<IProps>> = ({ activeStep, collapsed }) => {
  const { classes } = useStyles();
  const [t] = useTranslation('public');
  const { pathname } = useRouter();
  const steps = [t('creatingOrder.steps.0'), t('creatingOrder.steps.1'), t('creatingOrder.steps.2')];
  const step = STEP_BY_PATHNAME[pathname] ?? activeStep ?? 0;

  return (
    <CollapsibleStepper className={classes.stepper} collapsed={collapsed} activeStep={step}>
      {steps.map((label) => (
        <Step key={label}>
          <StepLabel
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            StepIconComponent={StepIcon}
          >
            {label}
          </StepLabel>
        </Step>
      ))}
    </CollapsibleStepper>
  );
};

export default memo(CheckoutCollapsibleStepper);
